import {
  CloseOutlined,
  DownCircleOutlined,
  EditOutlined,
  ReloadOutlined,
  UpCircleOutlined,
} from "@ant-design/icons";
import { Button, Col, Modal, Tooltip, notification } from "antd";
import moment from "moment";

import React, { useEffect, useRef, useState } from "react";
import Carousel from "react-elastic-carousel";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import CreateProject from "../../components/CreateProject/CreateProject";
import CreateProjectMain from "../../components/CreateProject/CreateProjectMain";
import SearchModal from "../../components/Search";
import {
  commonConstants,
  roleBasedAccessControlConstants,
} from "../../constants";
import "../../global-styles/antd-custom.scss";
import {
  commonHelpers,
  projectListingHelper,
  rabcHelpers,
} from "../../helpers";

// Unit conversion function for wall thickness and pipe diameter
const convertLengthValue = (value, fromUnit, toUnit) => {
  if (!value || !fromUnit || !toUnit) return value;

  const numValue = parseFloat(value);
  if (isNaN(numValue)) return value;

  // Convert to inches first, then to target unit
  let valueInInches = numValue;

  // Convert from source unit to inches
  switch (fromUnit.toLowerCase()) {
    case 'cm':
      valueInInches = numValue * 2.54;
      break;
    case 'mm':
      valueInInches = numValue * 25.4;
      break;
    case 'in':
      valueInInches = numValue;
      break;
    default:
      return value;
  }

  // Convert from inches to target unit
  switch (toUnit.toLowerCase()) {
    case 'cm':
      return (valueInInches * 2.54).toFixed(2);
    case 'mm':
      return (valueInInches * 25.4).toFixed(2);
    case 'in':
      return valueInInches.toFixed(2);
    default:
      return value;
  }
};
import {
  TOGGLE_PROJECT_DETAILS,
  TOGGLE_PROJECT_LIST,
} from "../../redux/application/types";
import { GET_CURRENT_PROJECT } from "../../redux/data360Live_Project/types";
import {
  fetchWeldParametersRequest,
  fetchWeldParametersSuccess,
  updateRenamePasses,
} from "../../redux/data360Live_WeldParameter/action";

const ProjectListHeader = (props) => {
  const { reloadData, projectListData, setProjectList, handlerClearSearch } =
    props;
  const { t } = useTranslation();
  const reducerList = useSelector((state) => state);
  const dispatch = useDispatch();
  const carouselRef = useRef(null);
  const [searchText, onSearch] = useState("");
  const [searchNotFound, setSearchNotFound] = useState(false);
  const [showCarousel, setShowCarousel] = useState(false);
  const [showProjects, setShowProjects] = useState(true);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [projectToEdit, setProjectToEdit] = useState(null);
  const [downloadModalVisible, setDownloadModalVisible] = useState(false);
  const history = useHistory();

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const getCarouselConfig = (width) => {
    if (width < 100) return { itemsToShow: 1, itemsToScroll: 1 };
    if (width < 200) return { itemsToShow: 2, itemsToScroll: 1 };
    if (width < 500) return { itemsToShow: 3, itemsToScroll: 1 };
    if (width < 800) return { itemsToShow: 4, itemsToScroll: 1 };
    if (width < 1280) return { itemsToShow: 6, itemsToScroll: 1 };
    if (width < 1440) return { itemsToShow: 6, itemsToScroll: 1 };
    if (width < 1600) return { itemsToShow: 7, itemsToScroll: 1 };
    if (width < 1920) return { itemsToShow: 8, itemsToScroll: 1 };
    if (width < 2400) return { itemsToShow: 11, itemsToScroll: 1 };
    return { itemsToShow: 12, itemsToScroll: 1 };
  };

  const handleTitleClick = () => {
    setShowCarousel(!showCarousel);
    dispatch({ type: TOGGLE_PROJECT_DETAILS, payload: !showCarousel });
  };

  const handleProjectClick = () => {
    setShowProjects((showProjects) => !showProjects);
    dispatch({ type: TOGGLE_PROJECT_LIST, payload: !showProjects });
  };

  const weldParameters = useSelector(
    (state) => state.data360LiveWeldParameterReducer?.data
  );

  // Function to get unrenamed job numbers
  const getUnrenamedJobNumbers = () => {
    if (!weldParameters || !weldParameters.groupedByMachine) return [];

    const unrenamedJobs = [];
    Object.entries(weldParameters.groupedByMachine).forEach(
      ([machineType, machineArray]) => {
        machineArray.forEach((parameter) => {
          if (parameter.is_renamed === false) {
            unrenamedJobs.push({
              jobNumber: parameter.job_number,
              machineType: machineType,
              isRenamed: parameter.is_renamed,
            });
          }
        });
      }
    );
    return unrenamedJobs;
  };

  // Check if any weld parameter has is_renamed: false
  useEffect(() => {
    if (weldParameters && weldParameters.groupedByMachine) {
      const hasUnrenamedParameters = Object.values(
        weldParameters.groupedByMachine
      ).some((machineArray) => {
        return machineArray.some((parameter) => parameter.is_renamed === false);
      });

      if (hasUnrenamedParameters) {
        setDownloadModalVisible(true);
      }
    }
  }, [weldParameters]);

  const getSelectedProject = (project_id, project_name) => {
    const project = projectListData?.find(
      (item) =>
        item.project_id === project_id && item.project_name === project_name
    );

    if (project) {
      const { itemsToShow } = getCarouselConfig(windowWidth);
      const reversedList = projectListData.slice().reverse();

      const selectedIndex = reversedList.findIndex(
        (p) => p.project_id === project_id && p.project_name === project_name
      );

      if (selectedIndex !== -1) {
        const totalItems = reversedList.length;
        let targetIndex = selectedIndex;

        targetIndex = Math.min(targetIndex, totalItems - itemsToShow);

        dispatch({
          type: GET_CURRENT_PROJECT,
          payload: project,
        });

        dispatch(
          fetchWeldParametersRequest({
            project_id,
          })
        );

        if (carouselRef.current) {
          carouselRef.current.goTo(targetIndex);
        }
      }
    } else {
      // If project not found, try to find it by ID only (in case only name changed)
      const projectById = projectListData?.find(
        (item) => item.project_id === project_id
      );
      if (projectById) {
        dispatch({
          type: GET_CURRENT_PROJECT,
          payload: projectById,
        });
      }
    }
  };

  useEffect(() => {
    const selectedProject =
      reducerList?.data360LiveProjectReducer?.currentProject;
    if (selectedProject) {
      getSelectedProject(
        selectedProject.project_id,
        selectedProject.project_name
      );
    }
  }, [
    projectListData,
    reducerList?.data360LiveProjectReducer?.currentProject?.project_id,
    reducerList?.data360LiveProjectReducer?.currentProject?.project_name,
    dispatch,
    windowWidth,
    projectListData,
  ]);

  const carouselConfig = [
    { width: 100, ...getCarouselConfig(100) },
    { width: 200, ...getCarouselConfig(200) },
    { width: 500, ...getCarouselConfig(500) },
    { width: 800, ...getCarouselConfig(800) },
    { width: 1280, ...getCarouselConfig(1280) },
    { width: 1440, ...getCarouselConfig(1440) },
    { width: 1600, ...getCarouselConfig(1600) },
    { width: 1920, ...getCarouselConfig(1920) },
    { width: 2400, ...getCarouselConfig(2400) },
  ];

  const onCancel = () => {
    setSearchNotFound("");
  };

  const handleSearch = (searchTerm) => {
    onSearch(searchTerm);
  };

  const handleClickSearch = ({ callBack }) => {
    if (!searchText) {
      notification.warn({
        message: t("projectDetails.enterProjectName"),
      });

      return;
    }

    const filteredProjects =
      reducerList.data360LiveProjectReducer.projectDetails.projects?.filter(
        (project) =>
          project.project_name.toLowerCase().includes(searchText) ||
          project.project_number.toLowerCase().includes(searchText)
      );

    if (filteredProjects.length > 0) {
      setProjectList(filteredProjects);

      dispatch({
        type: GET_CURRENT_PROJECT,
        payload: {},
      });

      if (carouselRef.current) {
        carouselRef.current.goTo(0);
      }

      callBack();
    } else {
      notification.error({
        message: t("projectDetails.noProject") + searchText,
      });
    }
  };

  const formatWeldingSystems = (weldingSystem) => {
    const formatted = weldingSystem.map((item) => {
      switch (item.toLowerCase()) {
        case "iwm":
          return "IWM";
        case "m500":
          return "M-500";
        case "p625":
          return "P-625";
        case "p600z":
          return "P-600Z";
        default:
          return item.toUpperCase();
      }
    });

    return formatted.join(", ");
  };

  const getCarouselClassName = (projectListLength) => {
    return projectListLength <= 5 ? "fourflexslider h-100" : "flexslider h-100";
  };

  let roleOfUser = commonHelpers.getRoleOfUserFromReduxState(
    reducerList?.auth?.userDetails
  );

  let doesHavePermission = rabcHelpers.hasUserHasRole({
    roleName: roleOfUser,
    scope: roleBasedAccessControlConstants.scopes.project.edit,
  });

  return (
    <>
      {/* Download Weld Parameters Modal */}
      <Modal
        title="Downloading Weld Parameters"
        open={downloadModalVisible}
        onCancel={() => setDownloadModalVisible(false)}
        footer={[
          <Button key="no" onClick={() => setDownloadModalVisible(false)}>
            Cancel
          </Button>,
        ]}
        centered
        width={600}
        className="download-weld-parameters-modal"
      >
        <div>
          <p>
            A new Weld Parameter has been downloaded. Would you like to rename
            the pass names?
          </p>
          <div style={{ marginTop: 16 }}>
            {getUnrenamedJobNumbers().map((job) => (
              <div
                key={job.jobNumber}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "8px 0",
                  borderBottom: "1px solid #f0f0f0",
                }}
              >
                <span>
                  {job.jobNumber} ({job.machineType})
                </span>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      dispatch(
                        updateRenamePasses({
                          project_id:
                            reducerList?.data360LiveProjectReducer
                              ?.currentProject?.project_id,
                          job_number: job.jobNumber,
                          is_renamed: true,
                          cb: () => {
                            dispatch(fetchWeldParametersSuccess({}));
                            history.push("/data360Live");
                          },
                        })
                      );
                      setDownloadModalVisible(false);
                    }}
                    style={{
                      margin: "0 3px",
                      border: "1px solid white",
                      borderRadius: "18px",
                    }}
                  >
                    No
                  </Button>
                  <Button
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setDownloadModalVisible(false);
                      history.push({
                        pathname: "/renaming-pass",
                        state: {
                          jobNumber: job.jobNumber,
                          machineType: job.machineType,
                        },
                      });
                    }}
                    style={{
                      margin: "0 3px",
                      border: "1px solid white",
                      borderRadius: "18px",
                    }}
                  >
                    Yes
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Modal>
      <div className="project-carousel">
        <div className="project-list" onClick={handleProjectClick}>
          {!showProjects ? (
            <DownCircleOutlined className="downoutlined-icon-projectList" />
          ) : (
            <UpCircleOutlined className="upoutlined" />
          )}{" "}
          <h1 className="project-title ">{t("common.projects")}</h1>
          <h6 className="mx-3 mt-1">
            {!showProjects &&
            Object.keys(
              reducerList?.data360LiveProjectReducer?.currentProject || {}
            ).length ? (
              <b>{`${reducerList?.data360LiveProjectReducer?.currentProject?.project_number}\t-\t${reducerList?.data360LiveProjectReducer?.currentProject?.project_name}`}</b>
            ) : (
              ""
            )}
          </h6>
        </div>
        <div className="search-container d-flex">
          {searchText !== "" && searchNotFound === false && (
            <div className="top-bar-input d-flex justify-content-between align-items-center me-3">
              <span>{searchText}</span>{" "}
              <a
                className="export-link"
                onClick={() => {
                  onSearch("");
                  handlerClearSearch();
                }}
              >
                <CloseOutlined />
              </a>{" "}
            </div>
          )}
          <SearchModal
            onSearch={(e) => handleSearch(e.target.value.toLowerCase())}
            searchText={searchText}
            setValue={(val) => onSearch(val)}
            onCancel={onCancel}
            handleClickSearch={handleClickSearch}
            projectNumbers={projectListData}
          />
          {doesHavePermission && (
            <Tooltip placement="topLeft" title="Edit Project">
              <Button
                className="edit ms-3"
                shape="circle"
                disabled={
                  !Object.keys(
                    reducerList?.data360LiveProjectReducer?.currentProject || {}
                  ).length
                }
                onClick={() => {
                  const selectedProject =
                    reducerList?.data360LiveProjectReducer?.currentProject;
                  if (selectedProject?.project_id) {
                    setProjectToEdit(selectedProject);
                    setEditModalVisible(true);
                  }
                }}
              >
                <EditOutlined />
              </Button>
            </Tooltip>
          )}
          <Tooltip placement="topLeft" title="Reload">
            <Button
              className="refresh ms-3"
              shape="circle"
              onClick={() => {
                reloadData();
              }}
            >
              <ReloadOutlined />
            </Button>
          </Tooltip>
        </div>
      </div>
      <div>
        <div
          className={
            showProjects
              ? "project-list-carousel"
              : "project-list-carousel-hidden"
          }
        >
          <div className="flex-auto">
            <CreateProject />
          </div>
          {projectListData?.length > 0 && (
            <div className="flex-fill overflow-auto">
              <div className={getCarouselClassName(projectListData.length)}>
                <Carousel
                  ref={carouselRef}
                  pagination={false}
                  breakPoints={carouselConfig}
                >
                  {projectListData
                    .slice()
                    .reverse()
                    .map((data, index) => {
                      return (
                        <div
                          className="float-start slides-divs slides"
                          key={`${data.project_id}-${index}`}
                        >
                          <a
                            id={data.project_id}
                            onClick={() => {
                              getSelectedProject(
                                data.project_id,
                                data.project_name
                              );
                            }}
                            className={
                              data.project_id ===
                                reducerList?.data360LiveProjectReducer
                                  ?.currentProject?.project_id &&
                              data.project_name ===
                                reducerList?.data360LiveProjectReducer
                                  ?.currentProject?.project_name
                                ? "slides-items active"
                                : "slides-items"
                            }
                          >
                            <div className="text-center tooltip-parent">
                              {" "}
                              <span
                                className={
                                  data.project_id ===
                                    reducerList?.data360LiveProjectReducer
                                      ?.currentProject?.project_id &&
                                  data.project_name ===
                                    reducerList?.data360LiveProjectReducer
                                      ?.currentProject?.project_name
                                    ? "tooltip-text-active"
                                    : "tooltip-text"
                                }
                              >
                                {data.project_name.length > 20
                                  ? data.project_name
                                      .substring(0, 30)
                                      .concat(
                                        `\n ${data.project_name.substring(
                                          30,
                                          data.project_name.length
                                        )}`
                                      )
                                  : ""}
                              </span>
                              <span>
                                {data.project_name.length > 20
                                  ? data.project_name
                                      .substring(0, 19)
                                      .concat("...")
                                  : data.project_name}
                              </span>
                            </div>
                            <div className="text-center tooltip-parent">
                              {" "}
                              <span>
                                {data.project_number.length > 20
                                  ? data.project_number
                                      .substring(0, 19)
                                      .concat("...")
                                  : data.project_number}
                              </span>
                              <span
                                className={
                                  data.project_id ===
                                    reducerList?.data360LiveProjectReducer
                                      ?.currentProject?.project_id &&
                                  data.project_name ===
                                    reducerList?.data360LiveProjectReducer
                                      ?.currentProject?.project_name
                                    ? "tooltip-text-active-bottom"
                                    : "tooltip-text-bottom"
                                }
                              >
                                {data.project_number.length > 20
                                  ? data.project_number
                                      .substring(0, 30)
                                      .concat(
                                        `\n ${data.project_number.substring(
                                          30,
                                          data.project_number.length
                                        )}`
                                      )
                                  : ""}
                              </span>
                            </div>
                          </a>
                        </div>
                      );
                    })}
                </Carousel>
              </div>
            </div>
          )}
        </div>
        <div className="custom-line mb-3" />
      </div>
      {reducerList?.data360LiveProjectReducer?.currentProject?.project_id &&
      !showCarousel ? (
        <div className="project-details-hide" onClick={handleTitleClick}>
          <DownCircleOutlined className="downoutlined-icon" />
          <h6 className="p-details-title">
            {t("projectDetails.projectDetail")}
          </h6>
          <div className="custom-line mb-3 p-d" />
        </div>
      ) : (
        <div>
          <div className="p-d-f">
            {reducerList?.data360LiveProjectReducer?.currentProject
              ?.project_id && (
              <UpCircleOutlined
                onClick={handleTitleClick}
                className="upoutlined"
              />
            )}
          </div>
          <div className="project-detail-bar">
            <div className="project-detail-bar-items h-100 mb-3">
              {reducerList?.data360LiveProjectReducer?.currentProject
                ?.project_id && (
                <>
                  <div className="flex-fill overflow-auto">
                    <div className="flexslider-bt flexslider-small h-100">
                      <Carousel
                        pagination={false}
                        breakPoints={commonConstants.carouselConfig}
                      >
                        {projectListingHelper.displayFields?.map((project) => {
                          // Get unit for fields that have unitKey
                          const getTitleWithUnit = () => {
                            const baseTitle = t(`${project.name}`);
                            if (project.unitKey && reducerList?.data360LiveProjectReducer?.currentProject?.units?.[project.unitKey]) {
                              return `${baseTitle} (${reducerList.data360LiveProjectReducer.currentProject.units[project.unitKey]})`;
                            }
                            return baseTitle;
                          };

                          if (project.value === "welding_system") {
                            return (
                              <Col key={project.value} span={24}>
                                <div className="project-details-wrapper text-center">
                                  <p className="fw-medium title">
                                    {getTitleWithUnit()}
                                  </p>
                                  <div className="d-flex edit-session justify-content-center mb-2">
                                    <p className="mb-0">
                                      {Array.isArray(
                                        reducerList?.data360LiveProjectReducer
                                          ?.currentProject?.welding_systems
                                      ) &&
                                      reducerList?.data360LiveProjectReducer
                                        ?.currentProject?.welding_systems
                                        .length > 0
                                        ? formatWeldingSystems(
                                            reducerList
                                              ?.data360LiveProjectReducer
                                              ?.currentProject?.welding_systems
                                          )
                                        : "-"}
                                    </p>
                                  </div>
                                </div>
                              </Col>
                            );
                          } else if (
                            project.value === "welding_assignment_type"
                          ) {
                            return (
                              <Col key={project.value} span={24}>
                                <div className="project-details-wrapper text-center">
                                  <p className="fw-medium title">
                                    {getTitleWithUnit()}
                                  </p>
                                  <div className="d-flex edit-session justify-content-center mb-2">
                                    <p className="mb-0">
                                      {
                                        reducerList?.data360LiveProjectReducer
                                          ?.currentProject?.weldAssignmentType
                                      }
                                    </p>
                                  </div>
                                </div>
                              </Col>
                            );
                          } else if (
                            project.value === "start_date" ||
                            project.value === "end_date"
                          ) {
                            const dateValue =
                              reducerList?.data360LiveProjectReducer
                                ?.currentProject[project.value];
                            const formattedDate = dateValue
                              ? moment(new Date(dateValue)).format(
                                  "DD-MMM-YYYY"
                                )
                              : "-";
                            return (
                              <Col key={project.value} span={24}>
                                <div className="project-details-wrapper text-center">
                                  <p className="fw-medium title">
                                    {getTitleWithUnit()}
                                  </p>
                                  <div className="d-flex edit-session justify-content-center mb-2">
                                    <p className="mb-0"> {formattedDate}</p>
                                  </div>
                                </div>
                              </Col>
                            );
                          } else if (project.value === "welds_completed") {
                            const completed =
                              reducerList?.data360LiveProjectReducer
                                ?.currentProject?.welds_completed || 0;
                            const planned =
                              reducerList?.data360LiveProjectReducer
                                ?.currentProject?.welds_planned || 0;
                            return (
                              <Col key={project.value} span={24}>
                                <div className="project-details-wrapper text-center">
                                  <p className="fw-medium title">
                                    {getTitleWithUnit()}
                                  </p>
                                  <div className="d-flex edit-session justify-content-center mb-2">
                                    <p className="mb-0">{`${completed} of ${planned}`}</p>
                                  </div>
                                </div>
                              </Col>
                            );
                          } else if (project.value === "iwm") {
                            const iwmValue =
                              reducerList?.data360LiveProjectReducer
                                ?.currentProject[project.value];
                            return (
                              <Col key={project.value} span={24}>
                                <div className="project-details-wrapper text-center">
                                  <p className="fw-medium title">
                                    {getTitleWithUnit()}
                                  </p>
                                  <div className="d-flex edit-session justify-content-center mb-2">
                                    <p className="mb-0">
                                      {iwmValue
                                        ? t("common.yes")
                                        : t("common.no")}
                                    </p>
                                  </div>
                                </div>
                              </Col>
                            );
                          } else if (project.value === "num_stations") {
                            const numstations =
                              reducerList?.data360LiveProjectReducer
                                ?.currentProject[project.value];
                            return (
                              <Col key={project.value} span={24}>
                                <div className="project-details-wrapper text-center">
                                  <p className="fw-medium title">
                                    {getTitleWithUnit()}
                                  </p>
                                  <div className="d-flex edit-session justify-content-center mb-2">
                                    <p className="mb-0">
                                      {Math.floor(numstations)}
                                    </p>
                                  </div>
                                </div>
                              </Col>
                            );
                          } else {
                            let fieldValue =
                              reducerList?.data360LiveProjectReducer
                                ?.currentProject[project.value] || "-";

                            // Apply unit conversion for wall thickness and pipe diameter
                            if ((project.value === "pipe_thickness" || project.value === "pipe_diameter") &&
                              project.unitKey &&
                              fieldValue !== "-" &&
                              reducerList?.data360LiveProjectReducer?.currentProject?.units?.[project.unitKey]) {
                              const currentUnit = reducerList.data360LiveProjectReducer.currentProject.units[project.unitKey];
                              const targetUnit = "in"; // Always convert to inches for display

                              if (currentUnit !== targetUnit) {
                                fieldValue = convertLengthValue(fieldValue, currentUnit, targetUnit);
                              }
                            }

                            return (
                              <Col key={project.value} span={24}>
                                <div className="project-details-wrapper text-center">
                                  <p className="fw-medium title">
                                    {getTitleWithUnit()}
                                  </p>
                                  <div className="d-flex edit-session justify-content-center mb-4">
                                    <div className="text-center tooltip-display-parent">
                                      <p className="mb-0">
                                        {" "}
                                        {fieldValue.length > 19
                                          ? fieldValue
                                              .substring(0, 15)
                                              .concat("...")
                                          : fieldValue}
                                      </p>
                                      <span className="tooltip-display-text">
                                        {fieldValue.length > 15
                                          ? fieldValue
                                              .substring(0, 30)
                                              .concat(
                                                `\n ${fieldValue.substring(
                                                  30,
                                                  fieldValue.length
                                                )}`
                                              )
                                          : ""}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </Col>
                            );
                          }
                        })}
                      </Carousel>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Project Edit Modal */}
      {projectToEdit && (
        <CreateProjectMain
          isModalVisible={editModalVisible}
          handleCancel={() => {
            setEditModalVisible(false);
            setProjectToEdit(null);
          }}
          isEditMode={true}
          initialData={{ projectInfo: projectToEdit }}
        />
      )}
    </>
  );
};

export default ProjectListHeader;
